import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import APIS from "@service/api";
import { getMultinationalTenant, getUser } from "@utils";
import { useEffect, useMemo, useRef, useState } from "react";

export const sumsubApi = createApi({
	reducerPath: "sumsubApi",
	refetchOnFocus: false,
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: false,
	baseQuery: fetchBaseQuery({
		baseUrl: APIS["sumsub-api"],
		prepareHeaders: (headers) => {
			const user = getUser();
			const username = user?.Username || user?.username;

			if (user?.token) {
				headers.set("Authorization", `Bearer=${user.token}`);
			}

			if (username) {
				headers.set("X-Atom-Username", String(username).toLowerCase().trim());
			}

			headers.set("X-Atom-Tenant", window.SMB_TENANT || getMultinationalTenant());

			return headers;
		},
	}),
	endpoints: (builder) => ({
		fetchApplicantData: builder.mutation({
			query: ({ username, email, country, tin, dob, externalUserId }) => ({
				url: "applicant-data",
				method: "POST",
				body: {
					"username": username,
					"country": country,
					"tin": tin,
					"dob": dob,
					"levelName": "cpf_verification_br",
					"externalUserId": externalUserId,
					"email": email,
				},
			}),
			transformResponse: (response) => response.message,
		}),
		createSumSubWebLink: builder.query({
			query: (body) => ({
				url: "create-weblink",
				method: "POST",
				body,
			}),
			transformResponse: (response) => response.message,
		}),
		createSumSubToken: builder.query({
			query: (body) => ({
				url: "create-token",
				method: "POST",
				body,
			}),
			transformResponse: (response) => response.message,
		}),
		extractSumSubTokenParameter: builder.query({
			query: ({ token, parameterName = "timestamp", username }) => ({
				url: "extract-token-parameter",
				method: "POST",
				body: { token, parameterName },
				headers: {
					"X-Atom-Username": username,
					"X-Atom-Token": token,
				},
			}),
			transformResponse: (response) => response.message,
		}),
		validateSumSubToken: builder.mutation({
			query: ({ token, username }) => ({
				url: "validate-token",
				method: "POST",
				body: { token },
				headers: {
					"X-Atom-Username": username,
				},
			}),
		}),
		sumSubVerificationStatus: builder.mutation({
			query: ({ type, id, username, sumsubToken }) => ({
				url: "verification-status",
				method: "POST",
				body: { type, id },
				headers: {
					"X-Atom-Username": username,
					"X-Atom-Sumsub-Token": sumsubToken,
				},
			}),
			transformResponse: (response) => response.message,
		}),
	}),
});

let socketInstances = [];

export const useSumSubSocket = (
	{ token, signup = false, username, tenant },
	{ skip } = { skip: false }
) => {
	const payload = useMemo(() => {
		const headers = {
			username,
			token,
			signup: signup,
			service: "sumsub",
			tenant: tenant,
		};

		const base64 = btoa(JSON.stringify(headers))
			.replace(/\+/g, "-")
			.replace(/\//g, "_")
			.replace(/=+$/, "");

		return {
			url: APIS["sumsub-socket-api"],
			headers: base64,
		};
	}, [token, username, signup, tenant]);

	const [isOpen, setIsOpen] = useState(false);
	const [isError, setIsError] = useState(false);
	const [socketInstance, setSocketInstance] = useState(null);
	const [lastMessage, setLastMessage] = useState(null);
	const socketRef = useRef(null);
	const messageSequenceRef = useRef(0);

	useEffect(() => {
		return () => {
			while (socketInstances.length > 0) {
				const instance = socketInstances.pop();
				if (
					instance &&
					(instance.readyState === WebSocket.CONNECTING ||
						instance.readyState === WebSocket.OPEN)
				) {
					try {
						instance.close();
					} catch (error) {}
				}
			}
		};
	}, []);

	useEffect(() => {
		if (skip || !payload.url) {
			setIsError(false);
			setLastMessage(null);
			return () => {
				if (socketRef.current) {
					const currentSocket = socketRef.current;
					if (
						currentSocket.readyState === WebSocket.CONNECTING ||
						currentSocket.readyState === WebSocket.OPEN
					) {
						try {
							currentSocket.close();
						} catch (error) {}
					}
					socketRef.current = null;
					setSocketInstance(null);
				}
			};
		}

		setIsError(false);
		setLastMessage(null);
		setIsOpen(false);

		const instance = new WebSocket(payload.url, payload.headers);
		socketRef.current = instance;

		instance.onmessage = (event) => {
			if (socketRef.current === instance && instance.readyState === WebSocket.OPEN) {
				setLastMessage({
					sequence: ++messageSequenceRef.current,
					data: event.data,
				});
			}
		};

		instance.onerror = (error) => {
			if (socketRef.current === instance) {
				setIsError(true);
				console.error(error);
			}
		};

		instance.onopen = () => {
			if (socketRef.current === instance) {
				setIsOpen(true);
				socketInstances.push(instance);

				if (instance.readyState === WebSocket.OPEN) {
					try {
						instance.send(
							JSON.stringify({
								"action": "subscribe",
								"data": "sumsub",
							})
						);
					} catch (error) {}
				}
			}
		};

		instance.onclose = () => {
			if (socketRef.current === instance) {
				setIsOpen(false);
				socketRef.current = null;
				setSocketInstance(null);
			}
		};

		setSocketInstance(instance);

		return () => {
			if (socketRef.current === instance) {
				if (
					instance.readyState === WebSocket.CONNECTING ||
					instance.readyState === WebSocket.OPEN
				) {
					try {
						instance.close();
					} catch (error) {}
				}
				socketRef.current = null;
				setSocketInstance(null);
			}
		};
	}, [payload.url, payload.headers, skip]);

	const closeSocket = () => {
		const currentSocket = socketRef.current;
		if (currentSocket) {
			if (
				currentSocket.readyState === WebSocket.CONNECTING ||
				currentSocket.readyState === WebSocket.OPEN
			) {
				try {
					currentSocket.close();
				} catch (error) {}
			}
			socketRef.current = null;
			setSocketInstance(null);
		}
	};

	useEffect(() => {
		return () => {
			const currentSocket = socketRef.current;
			if (currentSocket) {
				if (
					currentSocket.readyState === WebSocket.CONNECTING ||
					currentSocket.readyState === WebSocket.OPEN
				) {
					try {
						currentSocket.close();
					} catch (error) {}
				}
				socketRef.current = null;
				setSocketInstance(null);
			}
		};
	}, []);

	return { isOpen, isError, lastMessage, socketInstance, closeSocket };
};

export const {
	useFetchApplicantDataMutation,
	useCreateSumSubTokenQuery,
	useCreateSumSubWebLinkQuery,
	useExtractSumSubTokenParameterQuery,
	useValidateSumSubTokenMutation,
	useSumSubVerificationStatusMutation,
} = sumsubApi;
